import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { useClickOutside } from 'hooks/click-outside';
import { useCombinedRefs } from 'hooks/combined-refs';
import { useKeydown } from 'hooks/key-down';
import PropTypes from 'prop-types';
import { CustomPicker } from 'react-color';
import { ChangeEvent } from 'utils/form-utils';

import styles from './ColorInput.module.scss';

const { Saturation, Hue } = require('react-color/lib/components/common');

const PICKER_FALLBACK = '#FFFFFF';

const pickerColor = value => (value == null || value === '' ? PICKER_FALLBACK : value);

export const ColorInput = ({
  name,
  initialColor = PICKER_FALLBACK,
  className,
  validators = {},
  inputProps = {},
  register = () => {},
}) => {
  const [color, setColor] = useState(() =>
    initialColor === null || initialColor === '' ? null : initialColor,
  );

  // Picker
  const [showPicker, setShowPicker] = useState(false);
  const componentRef = useRef(null);
  useClickOutside(componentRef, () => setShowPicker(false));
  useKeydown(e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setShowPicker(false);
    }
  }, window);

  // Be able to use ref with register
  const { ref, ...registerProps } = register(name, validators);
  const inputRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, inputRef);

  // Change
  useEffect(() => {
    if (!inputRef.current) return;

    const valueForInput = color == null || color === '' ? '' : color;
    const { value: previousValue } = inputRef.current;
    inputRef.current.value = valueForInput;
    if (valueForInput === '') return;

    if (previousValue !== valueForInput && registerProps?.onChange) {
      registerProps.onChange(ChangeEvent(name, valueForInput));
    }
  }, [color, inputRef]);

  useEffect(() => {
    if (initialColor === undefined) return;
    const next = initialColor === null || initialColor === '' ? null : initialColor;
    if (next !== color) setColor(next);
  }, [initialColor]);

  return (
    <div ref={componentRef} className={cn(styles.root, { [className]: className })}>
      <div
        className={styles.preview}
        style={{ backgroundColor: pickerColor(color) }}
        onClick={() => setShowPicker(!showPicker)}
      />
      <input
        ref={combinedRef}
        name={name}
        className={styles.input}
        placeholder="#FFF"
        {...registerProps}
        {...inputProps}
      />
      <Picker visible={showPicker} color={pickerColor(color)} onChange={e => setColor(e.hex)} />
    </div>
  );
};

ColorInput.propTypes = {
  register: PropTypes.func,
  name: PropTypes.string,
  validators: PropTypes.object,
  className: PropTypes.string,
  inputProps: PropTypes.object,
};

const Picker = CustomPicker(({ visible, ...props }) => (
  <div className={cn(styles.picker, { [styles.pickerVisible]: visible })}>
    <div className={styles.saturation}>
      <Saturation {...props} />
    </div>
    <div className={styles.hue}>
      <Hue {...props} />
    </div>
  </div>
));
